import { computed, onUnmounted, ref } from 'vue'

import { handleFileUpload, poll } from '@backmarket/front-upload'
import type { UploadEntry } from '@backmarket/front-upload/dist/types/types'
import { sellerCustomerCareAPI } from '@backmarket/http-api'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { getHttpBaseUrl } from '@backmarket/nuxt-module-http/getHttpBaseUrl'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'

/*
 * Private composable
 * Please refer to the `useUploadReceipt` composable for usage.
 *
 * Note: This logic has been extracted from useUploadReceipt as a workaround to ease unit testing:
 * Allowing unit test without the need to Mock the lifecycle function used in `useUploadReceipt`.
 */
export function useUploadFileReceiptFunctions() {
  const { openErrorToast, openSuccessToast } = useTheToast()
  const logger = useLogger()

  const isUploading = ref<boolean>(false)
  const uploadStatus = ref<'pending' | 'failed' | 'success'>()
  const cancelPoll = ref<() => void>()

  const isUploadPending = computed(() => {
    return uploadStatus.value === 'pending'
  })
  const isUploadFailed = computed(() => {
    return uploadStatus.value === 'failed'
  })
  const isUploadSuccess = computed(() => {
    return uploadStatus.value === 'success'
  })
  const isUploadPendingOrSuccess = computed(() => {
    return isUploadPending.value || isUploadSuccess.value
  })

  async function updateUploadFileStatus(
    uploadedFile: UploadEntry,
    errorMessage: string,
  ) {
    cancelPoll.value = await poll(
      `${getHttpBaseUrl()}/services/uploads`,
      uploadedFile.handle,
      {
        onError: (error) => {
          uploadStatus.value = 'failed'
          openErrorToast({ title: errorMessage })

          logger.error(`Polling error, ${error}`, {
            step: 'polling-error',
            subService: 'front-upload',
            timestamp: Date.now(),
            uploadEntry: uploadedFile,
            owners: ['bot-squad-seller-care-front'],
          })
        },
        onFailure: ({ status, statusReason }) => {
          uploadStatus.value = 'failed'
          openErrorToast({ title: errorMessage })

          logger.error('Failed to upload receipt', {
            status,
            statusReason,
            owners: ['bot-squad-seller-care-front'],
          })
        },
        onSuccess: () => {
          uploadStatus.value = 'success'
        },
        onTimeout: () => {
          uploadStatus.value = 'failed'

          openErrorToast({ title: errorMessage })
        },
      },
    )
  }

  async function uploadFile({
    file,
    orderId,
    successMessage,
    errorMessage,
  }: {
    file: File
    orderId: number
    successMessage: string
    errorMessage: string
  }) {
    isUploading.value = true
    uploadStatus.value = 'pending'

    try {
      const uploadedFile = await handleFileUpload(
        `${getHttpBaseUrl()}/services/uploads`,
        'care_merchant_invoice',
        { orderId },
        file,
      )

      if (uploadedFile.status === 'success') {
        await $httpFetch(
          sellerCustomerCareAPI.provideCustomerRequestPlatformInvoiceV2,
          {
            pathParams: {
              orderId,
            },
            body: {
              handle: uploadedFile.handle,
            },
          },
        )
        openSuccessToast({ title: successMessage })

        await updateUploadFileStatus(uploadedFile as UploadEntry, errorMessage)
      }
      if (uploadedFile.status === 'failed') {
        openErrorToast({ title: errorMessage })
        uploadStatus.value = 'failed'
      }
    } catch (error) {
      uploadStatus.value = 'failed'
      openErrorToast({ title: errorMessage })

      logger.error(`Error uploading receipt for order #${orderId}, ${error}`, {
        owners: ['bot-squad-seller-care-front'],
      })
    } finally {
      isUploading.value = false
    }
  }

  return {
    // uploadFile function to upload a file for a given orderId
    uploadFile,
    // cancelPoll ref to stop the poll
    cancelPoll,
    // Whether the upload is in progress or not
    isUploading,
    // Whether the upload has a "pending" state
    isUploadPending,
    // Whether the upload has a "failed" state
    isUploadFailed,
    // Whether the upload has a "success" state
    isUploadSuccess,
    // Whether the upload has either a "pending" or a "success" state
    isUploadPendingOrSuccess,
  }
}

/**
 * Polls the upload status of the uploaded file
 * This is a function that should not be called directly,
 * consumers should rely on status to know if the upload is pending, failed or success.
 * The poll is stopped when the component is unmounted.
 */
export function useUploadReceipt() {
  const {
    uploadFile,
    cancelPoll,
    isUploading,
    isUploadPending,
    isUploadFailed,
    isUploadSuccess,
    isUploadPendingOrSuccess,
  } = useUploadFileReceiptFunctions()

  onUnmounted(() => {
    if (cancelPoll.value) {
      cancelPoll.value()
    }
  })

  return {
    // uploadFile function to upload a file for a given orderId
    uploadFile,
    // Whether the upload is in progress or not
    isUploading,
    // Whether the upload has a "pending" state
    isUploadPending,
    // Whether the upload has a "failed" state
    isUploadFailed,
    // Whether the upload has a "success" state
    isUploadSuccess,
    // Whether the upload has either a "pending" or a "success" state
    isUploadPendingOrSuccess,
  }
}
